import { useId, useState } from 'react';
import type { Destination } from '../../types';
import { byStatusThenName, channelLabel, joinNames } from './channelDisplay';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}
    >
      <path d="M3.5 1.5L7 5l-3.5 3.5" />
    </svg>
  );
}

interface UploadChannelPickerProps {
  /** The org's channels, as fetched. Sorted here, not by the caller. */
  destinations: Destination[];
  /** Picked slugs. Owned by the caller, because it also validates and submits them. */
  selected: string[];
  onToggle: (slug: string) => void;
  /** True while the upload is in flight — the selection is already committed to it. */
  disabled?: boolean;
}

/**
 * The optional "this whole file is Shopify" control on the upload screen (SC-3148).
 *
 * ## Collapsed, and secondary, on purpose
 *
 * This sits on the first-run screen. "No channel" is the common and completely
 * legitimate answer — membership of nothing is the backend's safe default and
 * needs no ceremony — so the control must read as an offer, not as an unfinished
 * field. Nothing here is required, nothing is pre-checked, and the collapsed
 * state is one quiet line.
 *
 * ## No permission gate
 *
 * Every OTHER membership surface checks `manage_destination_membership`. This one
 * deliberately does not: the backend runs the upload param under CREATE_CATALOG,
 * widening what uploading already does rather than adding a capability. Anyone who
 * can upload a catalog can say where it goes. Adding a gate here would silently
 * remove the control from users the backend would have accepted.
 *
 * ## `all_entities` channels are shown but not selectable
 *
 * Such a destination compiles to MATCH_ALL server-side, so every row already
 * reaches it without carrying its id — membership on one is inert. Same treatment
 * as `ChannelBulkAction`: listed with the reason, no control. Listing it matters;
 * a channel that silently vanishes from the picker reads as a bug in the picker.
 */
export function UploadChannelPicker({
  destinations,
  selected,
  onToggle,
  disabled = false,
}: UploadChannelPickerProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  // Pickable channels first, `all_entities` ones after. Both are sorted the same
  // way, but interleaving them puts a checkbox-less row mid-column, which reads
  // as a failed render rather than as "this one needs no choice".
  const channels = [
    ...destinations.filter((d) => d.entity_scope !== 'all_entities').sort(byStatusThenName),
    ...destinations.filter((d) => d.entity_scope === 'all_entities').sort(byStatusThenName),
  ];
  const selectedNames = channels.filter((d) => selected.includes(d.slug)).map((d) => d.name);
  const pausedPicked = channels
    .filter((d) => selected.includes(d.slug) && !d.enabled)
    .map((d) => d.name);

  const summary =
    selectedNames.length > 0
      ? `Syncing with ${joinNames(selectedNames)}`
      : 'Sync with a channel (optional)';

  return (
    <div style={{ marginTop: 12, fontFamily: FONT, textAlign: 'left' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          width: '100%',
          padding: '7px 10px',
          borderRadius: 8,
          border: `1px solid ${t.border}`,
          background: 'transparent',
          color: selectedNames.length > 0 ? t.text : t.text2,
          fontFamily: FONT,
          fontSize: 12.5,
          fontWeight: 500,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <Caret open={open} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary}
        </span>
      </button>

      {open && (
        <div
          id={panelId}
          style={{
            border: `1px solid ${t.border}`,
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            padding: '10px 10px 8px',
            background: t.surfaceAlt,
          }}
        >
          {/* No "you can change this later" — the table's bulk assign needs
              `manage_destination_membership`, which uploading does not. */}
          <p style={{ margin: '0 0 8px', fontSize: 11.5, lineHeight: 1.45, color: t.text3 }}>
            Everything in this file will sync with the channels you pick.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {channels.map((d) =>
              d.entity_scope === 'all_entities' ? (
                <div key={d.id} style={{ padding: '4px 6px', fontSize: 12.5, color: t.text3 }}>
                  {channelLabel(d)}
                  <span style={{ display: 'block', fontSize: 11, marginTop: 1 }}>
                    Every product goes here already, so there’s nothing to pick.
                  </span>
                </div>
              ) : (
                <label
                  key={d.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 6px',
                    borderRadius: 6,
                    fontSize: 12.5,
                    color: selected.includes(d.slug) ? t.text : t.text2,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(d.slug)}
                    disabled={disabled}
                    onChange={() => onToggle(d.slug)}
                    style={{ accentColor: t.accent, margin: 0, flexShrink: 0, cursor: disabled ? 'default' : 'pointer' }}
                  />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {channelLabel(d)}
                  </span>
                </label>
              ),
            )}
          </div>

          {/* Named rather than generic: a paused channel accepts membership fine,
              it just won't act on it, and the user should know which one is quiet. */}
          {pausedPicked.length > 0 && (
            <p
              style={{
                margin: '8px 0 0',
                padding: '6px 8px',
                borderRadius: 6,
                background: t.amberBgSubtle,
                color: t.amberText,
                fontSize: 11.5,
                lineHeight: 1.45,
              }}
            >
              {joinNames(pausedPicked)} {pausedPicked.length === 1 ? 'is' : 'are'} paused — nothing
              will sync there until {pausedPicked.length === 1 ? 'it’s' : 'they’re'} switched on.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
